import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Box, Button, Stack, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Typography, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityRouter } from '../hooks/useEntityRouter';
import {
  listStores,
  createStore,
  updateStore,
  deleteStore,
  Store,
// 
  getAddressFixStats,
  fixStoreAddressesBatch,
  AddressFixStats,
  AddressFixResult,
  StoreFilters as StoreFiltersType,
} from '../services/storeService';
import { StoreCreate, StoreUpdate } from '../types/entities';
import DataTable from '../components/DataTable';
import FormDialog from '../components/FormDialog';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import StoreFilters from '../components/StoreFilters';
// // ;
// import { v4 as uuidv4 } from 'uuid';
// import axios from 'axios';
// // ;
// // ;
import WeeklyWorkSchedule, { WeeklyWorkScheduleValue } from '../components/WeeklyWorkSchedule';
import LocationInput from '../components/LocationInput';
import { listChains, Chain } from '../services/chainService';
import axios from "axios";

// Remove work_time_plan from schema and FormValues
const schema = yup.object({
  chain_id: yup.number().required('Chain is required'),
  location: yup.object({
    address: yup.string().required(),
    lat: yup.number().required(),
    lng: yup.number().required(),
  }).required(),
});

type FormValues = {
  chain_id: number;
  location: {
    address: string;
    lat: number;
    lng: number;
  };
};

// Remove StoreWithPlan interface

const StoresPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { entityId, navigateToEntity, navigateToList } = useEntityRouter('/stores');
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState<Store | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [workSchedule, setWorkSchedule] = useState<WeeklyWorkScheduleValue>({ weekly: {}, holidays: [] });
  
  // Filter state
  const [filters, setFilters] = useState<StoreFiltersType>({});
  
  // Address fixing state
  const [addressFixDialog, setAddressFixDialog] = useState(false);
  const [addressFixStats, setAddressFixStats] = useState<AddressFixStats | null>(null);
  const [fixingInProgress, setFixingInProgress] = useState(false);
  const [fixProgress, setFixProgress] = useState({ processed: 0, total: 0, fixed: 0, errors: 0 });
  

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { chain_id: 0, location: { address: '', lat: 0, lng: 0 } },
  });

  const { data: stores = [], isLoading } = useQuery({ 
    queryKey: ['stores', filters], 
    queryFn: () => listStores(filters) 
  });
  const { data: chains = [] } = useQuery({ queryKey: ['chains'], queryFn: listChains });

  // Load specific store if entityId is present
  useEffect(() => {
    if (entityId && stores.length > 0) {
      const store = stores.find((s: Store) => s.id === parseInt(entityId));
      if (store) {
        handleEdit(store);
      }
    }
  }, [entityId, stores]);
  // const _currentLocation = watch('location');

  const createMut = useMutation({
    mutationFn: createStore,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stores'] }),
  });
  const updateMut = useMutation({
    mutationFn: (payload: { id: number; data: StoreUpdate }) => updateStore(payload.id, payload.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stores'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteStore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stores'] }),
  });

  const onSubmit = (vals: FormValues) => {
    const { location, chain_id } = vals;
    const chain = chains.find((c: Chain) => c.id === chain_id);
    if (editData) {
      const updateVals: StoreUpdate = {
        address: location.address,
        lat: location.lat,
        lng: location.lng,
        chain_id,
        chain_name: chain ? chain.name : '',
        work_schedule: JSON.stringify(workSchedule),
      };
      updateMut.mutate({ id: editData.id, data: updateVals });
    } else {
      const createVals: StoreCreate = {
        address: location.address,
        lat: location.lat,
        lng: location.lng,
        chain_id,
        chain_name: chain ? chain.name : '',
        work_schedule: JSON.stringify(workSchedule),
        settlement_id: null,
        supervisor_id: null,
      };
      createMut.mutate(createVals);
    }
    setOpenForm(false);
    // Navigate back to list view after saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    // Navigate back to list view when closing without saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleAdd = () => {
    reset({ chain_id: 0, location: { address: '', lat: 0, lng: 0 } });
    setWorkSchedule({ weekly: {}, holidays: [] });
    setEditData(null);
    setOpenForm(true);
  };

  const handleEdit = (row: Store) => {
    let schedule: WeeklyWorkScheduleValue = { weekly: {}, holidays: [] };
    if (row.work_schedule) {
      try {
        schedule = typeof row.work_schedule === 'string' ? JSON.parse(row.work_schedule) : row.work_schedule;
      } catch { schedule = { weekly: {}, holidays: [] }; }
    }
    setEditData(row);
    reset({
      chain_id: row.chain_id ?? undefined,
      location: { address: row.address, lat: row.lat ?? 0, lng: row.lng ?? 0 },
    });
    setWorkSchedule(schedule);
    setOpenForm(true);
  };

  const handleRowClick = (row: Store) => {
    navigateToEntity(row.id);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post('/api/v1/stores/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    } catch (err) {
      alert('Failed to upload CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Address fixing functions
  const handleFixAddressesClick = async () => {
    try {
      const stats = await getAddressFixStats();
      setAddressFixStats(stats);
      setAddressFixDialog(true);
    } catch (_error) {
      alert('Failed to get address fix statistics');
      console.error('Address fix stats error:', _error);
    }
  };

  const handleFixAddresses = async () => {
    if (!addressFixStats || addressFixStats.stores_without_settlement === 0) return;
    
    setFixingInProgress(true);
    setFixProgress({ processed: 0, total: addressFixStats.stores_without_settlement, fixed: 0, errors: 0 });
    
    try {
      let startId = 0;
      const batchSize = 50;
      let totalProcessed = 0;
      let totalFixed = 0;
      let totalErrors = 0;
      
      while (totalProcessed < addressFixStats.stores_without_settlement) {
        const result: AddressFixResult = await fixStoreAddressesBatch(batchSize, startId);
        
        totalProcessed += result.processed;
        totalFixed += result.fixed;
        totalErrors += result.total_errors;
        
        setFixProgress({
          processed: totalProcessed,
          total: addressFixStats.stores_without_settlement,
          fixed: totalFixed,
          errors: totalErrors
        });
        
        // If no more stores to process, break
        if (result.processed === 0 || !result.next_start_id) {
          break;
        }
        
        startId = result.next_start_id;
        
        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Refresh the stores list
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      
      // Update stats
      const newStats = await getAddressFixStats();
      setAddressFixStats(newStats);
      
      alert(`Address fixing completed!\nProcessed: ${totalProcessed}\nFixed: ${totalFixed}\nErrors: ${totalErrors}`);
      
    } catch (_error) {
      alert('Failed to fix addresses. See console for details.');
      console.error('Address fixing error:', _error);
    } finally {
      setFixingInProgress(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'chain_name', headerName: t('chains.title'), width: 150 },
      { field: 'address', headerName: t('stores.address'), flex: 1 },
      { 
        field: 'region', 
        headerName: t('stores.region'), 
        width: 120,
        renderCell: (params: any) => params.row.settlement?.region?.name || '-'
      },
      { 
        field: 'settlement', 
        headerName: t('stores.city'), 
        width: 120,
        renderCell: (params: any) => params.row.settlement?.name || '-'
      },
      {
        field: 'work_schedule',
        headerName: t('stores.workingHours'),
        flex: 2,
        renderCell: (params: any) => {
          let value: WeeklyWorkScheduleValue | null = null;
          try {
            value = typeof params.row.work_schedule === 'string'
              ? JSON.parse(params.row.work_schedule)
              : params.row.work_schedule;
          } catch {
            value = null;
          }
          if (!value) return '-';
          const weekdayKeys = ['1', '2', '3', '4', '5', '6', '0'];
          const times = weekdayKeys.map((k, _) => {
            const periods = value!.weekly[k] || [];
            const isWeekend = k === '6' || k === '0';
            return (
              <span key={k} style={isWeekend ? { background: '#fffbe6', borderRadius: 3, padding: '0 4px', marginLeft: 2 } : {}}>
                {periods.length === 0 ? '-' : periods.map((p: any) => `${p.start}-${p.end}`).join(',')}
              </span>
            );
          });
          // Add generic holiday if present
          let holidayStr = '';
          const genericHoliday = (value.holidays || []).find(h => h.date === 'public_holiday');
          if (genericHoliday && genericHoliday.periods.length > 0) {
            holidayStr = 'H: ' + genericHoliday.periods.map((p: any) => `${p.start}-${p.end}`).join(',');
          }
          return (
            <span style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
              {times.flatMap((t, i) => i === 0 ? [t] : [<span key={`sep-${i}`}> | </span>, t])}
              {holidayStr ? <span style={{ background: '#e6f9e6', borderRadius: 3, padding: '0 4px', marginLeft: 2 }}>{' | ' + holidayStr}</span> : ''}
            </span>
          );
        },
      },
      {
        field: 'actions',
        headerName: t('stores.actions'),
        width: 120,
        sortable: false,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteId(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [handleEdit]
  );

  const handleFiltersChange = (newFilters: StoreFiltersType) => {
    setFilters(newFilters);
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>{t('stores.title')}</h2>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {t('stores.add')}
          </Button>
          <Button variant="outlined" onClick={handleUploadClick} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<BuildIcon />} 
            onClick={handleFixAddressesClick}
            color="secondary"
          >
            Fix Addresses
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Stack>
      </Stack>

      <StoreFilters filters={filters} onFiltersChange={handleFiltersChange} />

      <DataTable 
        rows={stores} 
        columns={columns} 
        loading={isLoading} 
        onRowDoubleClick={handleEdit}
        onRowClick={handleRowClick}
      />

      <FormDialog open={openForm} title={editData ? t('stores.edit') : t('stores.add')} onClose={handleCloseForm} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} pt={1}>
          <Controller
            name="chain_id"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Chain"
                {...field}
                fullWidth
                error={!!errors.chain_id}
                helperText={errors.chain_id?.message}
              >
                {chains.map((chain: Chain) => (
                  <option key={chain.id} value={chain.id}>{chain.name}</option>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="location"
            control={control}
            render={({ field }) => {
              const chain = chains.find((c: Chain) => c.id === watch('chain_id'));
              return (
                <LocationInput
                  address={field.value.address}
                  lat={field.value.lat}
                  lng={field.value.lng}
                  onChange={field.onChange}
                  compact
                  markerLabel={chain ? chain.name : ''}
                />
              );
            }}
          />
          <WeeklyWorkSchedule value={workSchedule} onChange={setWorkSchedule} holidayMode="single" />
        </Stack>
      </FormDialog>

      <ConfirmDeleteDialog
        open={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteMut.mutate(deleteId);
          setDeleteId(null);
        }}
      />

      {/* Address Fix Dialog */}
      <Dialog open={addressFixDialog} onClose={() => setAddressFixDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Fix Store Addresses</DialogTitle>
        <DialogContent>
          {addressFixStats && (
            <Stack spacing={2}>
              <Typography variant="body1">
                This will extract geographic information from store addresses and create necessary 
                country/region/settlement records to link stores properly.
              </Typography>
              
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Statistics:</Typography>
                <Typography>• Total stores: {addressFixStats.total_stores}</Typography>
                <Typography>• Stores with settlement links: {addressFixStats.stores_with_settlement}</Typography>
                <Typography>• Stores needing fixes: {addressFixStats.stores_without_settlement}</Typography>
                <Typography>• Currently fixed: {addressFixStats.percentage_fixed}%</Typography>
              </Box>

              {addressFixStats.stores_without_settlement === 0 ? (
                <Alert severity="success">
                  All stores already have settlement links! No fixes needed.
                </Alert>
              ) : (
                <>
                  {addressFixStats.examples_needing_fix.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Examples of stores needing fixes:
                      </Typography>
                      {addressFixStats.examples_needing_fix.slice(0, 3).map((store) => (
                        <Typography key={store.id} variant="body2" sx={{ ml: 2 }}>
                          • Store {store.id}: {store.address}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {fixingInProgress && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Processing stores... ({fixProgress.processed}/{fixProgress.total})
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={(fixProgress.processed / fixProgress.total) * 100} 
                      />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Fixed: {fixProgress.fixed} | Errors: {fixProgress.errors}
                      </Typography>
                    </Box>
                  )}

                  <Alert severity="info">
                    This process will run in batches to avoid timeouts. It may take several minutes 
                    for large datasets.
                  </Alert>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressFixDialog(false)} disabled={fixingInProgress}>
            Cancel
          </Button>
          {addressFixStats && addressFixStats.stores_without_settlement > 0 && (
            <Button 
              onClick={handleFixAddresses} 
              variant="contained"
              disabled={fixingInProgress}
            >
              {fixingInProgress ? 'Fixing...' : `Fix ${addressFixStats.stores_without_settlement} Stores`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoresPage; 